// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    DoNotFollowLinks,
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import loadBlogs from "@vanmeter/App/Handlers/BlogsList/LoadBlogs";
import updateFields from "@vanmeter/App/Handlers/BlogsList/UpdateFields";
import { useQueryParams } from "@vanmeter/App/Hooks/useQueryParams";
import { IBlogListParams } from "@vanmeter/App/Services/BlogListService";
import { CustomAppState, getBlogsListDataView } from "@vanmeter/App/Store/Reducers";
import React, { useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const location = getLocation(state);

    return {
        parameters: state.xBlogsList.params,
        location,
        blogsDataView: getBlogsListDataView(state),
    };
};

const mapDispatchToProps = {
    loadBlogs,
    updateFields,
};

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const VmiBlogListPage = ({ id, parameters, location, updateFields, loadBlogs, blogsDataView }: Props) => {
    const isMounted = useRef(false);
    const [queryParams, setQueryParams] = useQueryParams<IBlogListParams>(location);

    useEffect(() => {
        if (location.search) {
            updateFields({ ...queryParams });
        }
        isMounted.current = true;

        return () => {
            updateFields({ category: "", blogType: "", sortBy: "newestfirst", page: 1, pageSize: 6 });
        };
    }, []);

    useEffect(() => {
        if (!isMounted.current) return;
        updateFields({ ...queryParams });
    }, [queryParams]);

    // Sync store params back to URL when parameters change
    useEffect(() => {
        if (!isMounted.current) return;
        if (!blogsDataView.isLoading) {
            setQueryParams(parameters);
        }
    }, [parameters]);

    useEffect(() => {
        if (!isMounted.current) return;

        if (!blogsDataView.value && !blogsDataView.isLoading) {
            loadBlogs(parameters);
        }
    }, [parameters]);

    return (
        <Page>
            <Zone contentId={id} zoneName="Content" />
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(VmiBlogListPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "Content",
        allowedChildren: ["VmiBlogDetailsPage", "VmiFolderPage"],
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            {
                ...HorizontalRule,
                sortOrder: 199,
            },
            HideHeader,
            HideFooter,
            HideFromSearchEngines,
            DoNotFollowLinks,
            HideFromSiteSearch,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
        ],
    },
};

export default pageModule;

export const VmiBlogListPageContext = "VmiBlogListPage";
